import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { noop } from 'lodash-es';
import { Button, ConfirmationModal, DropdownMenu, DropdownMenuItem, FilterPickerGroup, Page, PageContentHeader, PageEmptyState, PageErrorState, Row, TableList, Typography } from 'seagull';
import { ChevronDown, Download, Import } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useOverlayContext } from '~/contexts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useClientArchiveRestoreDelete } from '~/hooks/useClientArchiveRestoreDelete';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ClientDetailPageCustomersOld } from '~/pages/ClientDetailPage/ClientDetailPageCustomersOld';
import { useOpenImportClientsModal } from '~/pages/ImportClients/hooks/useOpenImportClientsModal';
import { ClientRow } from './ClientRow/ClientRow';
import { ClientsBulkActionResultModal } from './ClientsBulkActionResultModal';
import { ClientsBulkActions } from './ClientsBulkActions';
import { ALL_CLIENT_STATUSES, ALL_CLIENT_TYPES, filterClients, sanitizeFilterValues, sortClients, toClient } from './ClientsPage.utils';
import { useExportClientsCsv } from './hooks/useExportClientsCsv';
import { useRowSelection } from './hooks/useRowSelection';
export const clientsPageTextsPath = 'client.dashboard';
export const GetClientsPageOldTID = (parentTestId)=>getTestIds(parentTestId);
const sortIds = [
    'clientName',
    'status',
    'type',
    'contacts'
];
const columnDefs = [
    {
        width: [
            140,
            360
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_name'
        }
    },
    {
        width: [
            100,
            100
        ],
        sortable: true,
        mobile: {
            inline: true
        },
        bi: {
            name: 'sort_clients_by_status'
        }
    },
    {
        width: [
            120,
            120
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_type'
        }
    },
    {
        width: [
            160,
            460
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_contacts'
        }
    }
];
function ClientsListView(param) {
    let { clients, refresh } = param;
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageOldTID(), []);
    const isMobileDevice = useIsMobileForHomePage();
    const navigate = useNavigate();
    const openCreateClient = useOpenCreateClientModal();
    const openEditClient = useOpenEditClientModal();
    const { exportingAll, exportingSelected, handleExportAll, handleExportSelected } = useExportClientsCsv();
    const openImportClients = useOpenImportClientsModal({
        onExportClients: handleExportAll
    });
    const { onArchive, onRestore, onDelete } = useClientArchiveRestoreDelete();
    const handleViewClient = useCallback((client)=>navigate(routes.client(client.id)), []);
    const handleEditClient = useCallback((client)=>openEditClient({
            clientId: client.id,
            initialName: client.name,
            initialClientType: client.type
        }), [
        openEditClient
    ]);
    const { showModal } = useOverlayContext();
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { t: importClientsTexts } = useLocaleKeys('client.import');
    const { t: bulkDeleteConfirmationTexts } = useLocaleKeys('client.dashboard.bulkDeleteConfirmation');
    const { t: columnHeaderTexts } = useLocaleKeys('client.dashboard.columnHeaders');
    const { t: statusFilterTexts } = useLocaleKeys('client.dashboard.statusFilter');
    const { t: typeFilterTexts } = useLocaleKeys('client.dashboard.typeFilter');
    const { t: emptyStateTexts } = useLocaleKeys('client.dashboard.emptyState');
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const { urlState: { search, status: rawStatusFilterValue, type: rawTypeFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                'active'
            ],
            multi: true
        },
        type: {
            defaultValue: ALL_CLIENT_TYPES,
            multi: true
        }
    });
    const statusFilterValue = useMemo(()=>sanitizeFilterValues(rawStatusFilterValue, ALL_CLIENT_STATUSES), [
        rawStatusFilterValue
    ]);
    const typeFilterValue = useMemo(()=>sanitizeFilterValues(rawTypeFilterValue, ALL_CLIENT_TYPES), [
        rawTypeFilterValue
    ]);
    const statusFilterOptions = useMemo(()=>[
            {
                value: 'active',
                label: statusFilterTexts.active()
            },
            {
                value: 'archived',
                label: statusFilterTexts.archived()
            }
        ], []);
    const typeFilterOptions = useMemo(()=>[
            {
                value: 'business',
                label: typeFilterTexts.business()
            },
            {
                value: 'individual',
                label: typeFilterTexts.individual()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label(),
            type: typeFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: statusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const statuses = value;
                        return !Array.isArray(statuses) || statuses.length === 0 || statuses.length === statusFilterOptions.length;
                    }
                }
            },
            type: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: typeFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const types = value;
                        return !Array.isArray(types) || types.length === 0 || types.length === typeFilterOptions.length;
                    }
                }
            }
        }), [
        statusFilterOptions,
        typeFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: statusFilterValue.length === statusFilterOptions.length ? [] : statusFilterValue,
            type: typeFilterValue.length === typeFilterOptions.length ? [] : typeFilterValue
        }), [
        statusFilterValue,
        typeFilterValue,
        statusFilterOptions,
        typeFilterOptions
    ]);
    const headerItems = useMemo(()=>[
            {
                id: 'clientName',
                value: columnHeaderTexts.clientName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'type',
                value: columnHeaderTexts.type()
            },
            {
                id: 'contacts',
                value: columnHeaderTexts.contacts()
            }
        ], []);
    const sortOptions = useMemo(()=>[
            {
                id: 'clientName',
                label: columnHeaderTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnHeaderTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'type',
                label: columnHeaderTexts.type(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contacts',
                label: columnHeaderTexts.contacts(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], []);
    const filteredAndSorted = useMemo(()=>{
        var _sortIds_colIndex;
        const filtered = filterClients({
            clients,
            searchTerm: search,
            statusFilter: statusFilterValue,
            typeFilter: typeFilterValue
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortKey = (_sortIds_colIndex = sortIds[colIndex]) !== null && _sortIds_colIndex !== void 0 ? _sortIds_colIndex : sortIds[0];
        const sortDirection = sort === 'asc' ? 'desc' : 'asc';
        return sortClients({
            clients: filtered,
            sortKey,
            sortDirection
        });
    }, [
        clients,
        search,
        statusFilterValue,
        typeFilterValue,
        tableSort
    ]);
    const { selected, numSelected, numVisibleSelected, setSelected, onToggleAllSelection, getSelectedIds, clearSelection } = useRowSelection({
        items: filteredAndSorted
    });
    const onBulkActionComplete = useCallback(()=>{
        clearSelection();
        refresh();
    }, [
        clearSelection,
        refresh
    ]);
    const hasSharedClientSelected = clients.some((client)=>client.isMultiVendorCustomer && selected[client.id] === true);
    const onBulkExport = ()=>handleExportSelected(getSelectedIds());
    const onBulkArchive = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        await showModal({
            biName: 'clients_bulk_action_result_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ClientsBulkActionResultModal, {
                    actionType: "archive",
                    ids: ids,
                    onClose: ()=>dismissModal(true),
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    const onBulkDelete = async ()=>{
        const ids = getSelectedIds();
        if (ids.length === 0) return;
        const confirmed = await showModal({
            biName: 'clients_dashboard_bulk_delete_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'clients_bulk_delete_confirm'
                        },
                        secondaryButton: {
                            name: 'clients_bulk_delete_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: bulkDeleteConfirmationTexts.title(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: bulkDeleteConfirmationTexts.content({
                            count: ids.length
                        })
                    }),
                    primaryButtonLabel: bulkDeleteConfirmationTexts.primaryButton(),
                    secondaryButtonLabel: bulkDeleteConfirmationTexts.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        await showModal({
            biName: 'clients_bulk_action_result_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ClientsBulkActionResultModal, {
                    actionType: "delete",
                    ids: ids,
                    onClose: ()=>dismissModal(true),
                    onComplete: onBulkActionComplete
                });
            }
        });
    };
    const resetKey = JSON.stringify({
        search,
        statusFilterValue,
        typeFilterValue,
        tableSort
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef,
        resetKey
    });
    const onSortChange = (sort)=>{
        changeSort(sort);
    };
    const handleTableFiltersRefresh = (filters, sort)=>{
        const selectedStatus = filters.status;
        const newStatus = (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : statusFilterOptions.map((opt)=>opt.value);
        const selectedType = filters.type;
        const newType = (selectedType === null || selectedType === void 0 ? void 0 : selectedType.length) ? selectedType : ALL_CLIENT_TYPES;
        changeUrlState({
            status: newStatus,
            type: newType
        });
        if (sort !== undefined) {
            changeSort(sort);
        }
    };
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: statusFilterOptions.map((opt)=>opt.value),
            type: ALL_CLIENT_TYPES
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: TID.Root(),
        ref: scrollElRef,
        onRefresh: refresh,
        header: clientsPageTexts.title(),
        actions: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            children: [
                /*#__PURE__*/ _jsxs(DropdownMenu, {
                    testId: TID.MoreActions.Root(),
                    target: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clients_more_actions'
                        },
                        skin: "primaryOutline",
                        testId: TID.MoreActions.Button(),
                        suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                        children: clientsPageTexts.moreActions()
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'import_clients'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Download, {}),
                            testId: TID.MoreActions.ImportItem(),
                            onClick: openImportClients,
                            children: importClientsTexts.entryButton()
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            intent: Intent.ExportClientsToCsv,
                            bi: {
                                name: 'clients_export_all_csv'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Import, {}),
                            testId: TID.MoreActions.ExportAllItem(),
                            onClick: handleExportAll,
                            disabled: exportingAll,
                            children: clientsPageTexts.exportClients()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'create_client'
                    },
                    testId: TID.CreateButton(),
                    onClick: ()=>openCreateClient(),
                    children: clientsPageTexts.createClient()
                })
            ]
        }),
        onScroll: onScroll,
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: tableSort,
            onSortChange: onSortChange,
            enableRowSelection: !isMobileDevice,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: filteredAndSorted.length,
            numSelectedRows: numVisibleSelected,
            onToggleAllSelection: onToggleAllSelection,
            children: [
                /*#__PURE__*/ _jsxs(PageContentHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                            style: {
                                marginBottom: 24
                            },
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: TID.Filters(),
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: noop,
                                    onRefresh: handleTableFiltersRefresh,
                                    search: {
                                        bi: {
                                            name: 'clients_search'
                                        },
                                        value: search,
                                        testId: TID.Search(),
                                        onChange: (event)=>{
                                            changeUrlState({
                                                search: event.target.value
                                            });
                                        },
                                        onClear: ()=>changeUrlState({
                                                search: ''
                                            })
                                    },
                                    mobile: {
                                        sortOptions
                                    }
                                }),
                                !isMobileDevice && /*#__PURE__*/ _jsx(ClientsBulkActions, {
                                    testId: TID.BulkActions(),
                                    numSelected: numSelected,
                                    hasSharedClientSelected: hasSharedClientSelected,
                                    onArchive: onBulkArchive,
                                    onDelete: onBulkDelete,
                                    onExport: onBulkExport,
                                    exporting: exportingSelected
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            testId: TID.TableHeader()
                        })
                    ]
                }),
                filteredAndSorted.length ? paginatedData.map((client)=>/*#__PURE__*/ _jsx(ClientRow, {
                        client: client,
                        onEditClient: handleEditClient,
                        onViewClient: handleViewClient,
                        onArchiveClient: onArchive,
                        onRestoreClient: onRestore,
                        onDeleteClient: onDelete
                    }, client.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                    illustration: /*#__PURE__*/ _jsx(_Fragment, {}),
                    title: emptyStateTexts.title(),
                    message: emptyStateTexts.description(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_clients_filters'
                        },
                        testId: TID.ClearFiltersButton(),
                        onClick: onClearFilters,
                        children: emptyStateTexts.clearFilters()
                    })
                })
            ]
        })
    });
}
export function ClientsPageOld() {
    const { clients: clientModels, isLoading, error, refresh } = useClients({
        refreshOnHookMount: true
    });
    const clients = useMemo(()=>clientModels.map(toClient), [
        clientModels
    ]);
    const detailMatch = useMatch({
        path: routes.client(),
        end: false
    });
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageOldTID(), []);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: clientsPageTexts.title()
        });
    }
    if (detailMatch != null) {
        return /*#__PURE__*/ _jsx(ClientDetailPageCustomersOld, {
            clientId: detailMatch.params.clientId
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorState(),
            title: clientsPageTexts.title(),
            onRefresh: refresh,
            pullToRefresh: true
        });
    }
    return /*#__PURE__*/ _jsx(ClientsListView, {
        clients: clients,
        refresh: refresh
    });
}
